/**
 * Turning a failure row into words and a destination.
 *
 * A failure records `mini` and `resultId` and nothing else about where it came from, so every
 * surface that shows one has to do the same two translations: which of the two result resources
 * the id belongs to, and what the stored token is called in English. The dashboard card and the
 * list screen both needed it, which was the point to stop copying it.
 */

/** The HQ route to the result a failure belongs to. */
export const resultRouteFor = (mini: string, resultId: number): string =>
    mini === 'cs'
        ? `/compliance_sniffer_results/${ resultId }`
        : `/mystery_shopper_results/${ resultId }`;

/** The mini's name, as people say it rather than as it is stored. */
export const miniLabel = (mini: string): string =>
    mini === 'cs' ? 'Compliance Sniffer' : 'Mystery Shopper';

/**
 * The three ways scoring stops, in the order they are worth reading.
 *
 * One list, used for both the filter dropdown and the column text. Written twice they drift, and
 * a filter offering a name the column never shows is a screen that looks broken.
 */
export const STAGES: { id: string, name: string }[] = [
    { id: 'operator', name: 'Operator gave up' },
    { id: 'timeout', name: 'No answer' },
    { id: 'dispatch', name: 'Never sent' },
    // HQAPI-146: the scores came back and HQ-API would not write them.
    { id: 'refused', name: 'Scores refused' },
];

/**
 * How long a rescore may sit queued before HQ offers to send it again. The same 30 minutes as
 * HQ-API's `nok_processing.stuck_after_minutes`, after which a result still waiting on its scores
 * is called stuck.
 */
export const STUCK_RETRY_MINUTES: number = 30;

/**
 * Whether a queued rescore has gone quiet for long enough to offer another (HQAPI-146).
 *
 * A 'retrying' row disables Rescore, because a second press would clear grades the queued attempt
 * may be writing. But a row whose job was lost stays 'retrying' for ever: the stuck sweep only
 * looks at results in 'scoring', so one moved anywhere else, by hand or otherwise, is never
 * flagged again, and Rescore stayed disabled with no way out. HQ-API already accepts a rescore of
 * a 'retrying' row; this is only when HQ offers one.
 */
export const isStuckRetrying = (
    failure: { status: string, updatedAt?: string|null },
    now: Date = new Date(),
): boolean => {
    if (failure.status !== 'retrying' || !failure.updatedAt) {
        return false;
    }
    const since: number = new Date(failure.updatedAt).getTime();
    if (Number.isNaN(since)) {
        return false;
    }

    return now.getTime() - since >= STUCK_RETRY_MINUTES * 60 * 1000;
};

/**
 * How it failed, short enough for a chip or a table cell.
 *
 * An unrecognised stage is shown as itself rather than swallowed, so a backend that starts
 * recording a new one is visible here rather than blank.
 *
 * NokProcessingFailurePanel deliberately does not use this. It has room for a sentence and reads
 * better with one ("The Operator gave up after 3 tries"), where a column does not.
 */
export const stageLabel = (stage: string): string =>
    STAGES.find((entry): boolean => entry.id === stage)?.name ?? stage;

/**
 * Who started a failure's most recent rescore, short enough for a table cell.
 *
 * Blank until the row has been retried. An unrecognised source is shown as itself, for the same
 * reason as `stageLabel`.
 */
export const retrySourceLabel = (source?: string|null): string => {
    if (source === 'customer') {
        return 'Customer';
    }
    if (source === 'automatic') {
        return 'Automatic';
    }
    if (source === 'admin') {
        return 'Admin';
    }

    return source ?? '';
};
